import { useState, useCallback, useEffect, memo } from 'react';

import { Grid } from '@mui/material';
import {
	APIProvider,
	AdvancedMarker,
	Map,
	MapCameraChangedEvent,
	MapCameraProps,
	RenderingType,
} from '@vis.gl/react-google-maps';

import { officePathByBrand } from '@/utils';
import { Maybe } from '@/types';
import { BffFacility, FacilityBrand, BffBrandType } from '@/types/generated';
import { getFacilityDetailsUrl } from '@/components/GoogleMap/GoogleMap.helpers';
import { USA_CENTER_COORDINATES, USA_DEFAULT_DESKTOP_ZOOM, USA_DEFAULT_MOBILE_ZOOM } from '@/constants';

import { createSVGMarkerObject, getMarkerIcon } from '../StandardGoogleMap.helpers';

import { getGoogleMapStyles } from './GoogleMapSection.styles';

export interface IGoogleMapsSection {
	nearByFacilities: BffFacility[];
	facilityBasePath: string;
	isSmallScreen: boolean;
	currentFacilityLatitude: number;
	currentFacilityLongitude: number;
	mobileZoom: Maybe<number>;
	desktopZoom: Maybe<number>;
	facilityBrand: FacilityBrand | BffBrandType;
	themeId: string;
	mapCenter: { latitude: number; longitude: number };
	fullWidth?: boolean;
	googleMapId: string;
	googleMapApiKey: string;
	forceZoomOut?: boolean;
}

export default memo(function GoogleMapSection({
	nearByFacilities,
	facilityBasePath,
	isSmallScreen,
	currentFacilityLatitude,
	currentFacilityLongitude,
	mobileZoom,
	desktopZoom,
	facilityBrand,
	themeId,
	fullWidth,
	mapCenter,
	googleMapId,
	googleMapApiKey,
	forceZoomOut,
}: Readonly<IGoogleMapsSection>) {
	const [cameraProps, setCameraProps] = useState<MapCameraProps>({
		zoom: isSmallScreen ? (mobileZoom as number) : (desktopZoom as number),
		center: { lat: mapCenter.latitude, lng: mapCenter.longitude },
	});
	const handleCameraChange = useCallback((event: MapCameraChangedEvent) => setCameraProps(event.detail), []);

	useEffect(() => {
		if (forceZoomOut) {
			setCameraProps({
				zoom: isSmallScreen ? (USA_DEFAULT_MOBILE_ZOOM as number) : (USA_DEFAULT_DESKTOP_ZOOM as number),
				center: { lat: USA_CENTER_COORDINATES.latitude, lng: USA_CENTER_COORDINATES.longitude },
			});
		} else {
			setCameraProps({
				zoom: isSmallScreen ? (mobileZoom as number) : (desktopZoom as number),
				center: { lat: mapCenter.latitude, lng: mapCenter.longitude },
			});
		}
	}, [mapCenter.latitude, mapCenter.longitude, mobileZoom, desktopZoom, isSmallScreen, forceZoomOut]);

	const handleMarkerClick = (facilityUrl: string) => {
		const officeDetailsUrl = getFacilityDetailsUrl(facilityBasePath, facilityUrl);

		if (globalThis?.location) {
			globalThis.location.href = officeDetailsUrl;
		}
	};

	return (
		<APIProvider apiKey={googleMapApiKey}>
			<Grid item md={8} data-test-id="google_maps_map_section">
				<Map
					{...cameraProps}
					onCameraChanged={handleCameraChange}
					style={getGoogleMapStyles(isSmallScreen, fullWidth)}
					mapId={googleMapId}
					renderingType={RenderingType.VECTOR}
				>
					{nearByFacilities.map((eachFacility: BffFacility, index: number) => {
						const title: string = eachFacility.name || '';

						const facilityUrl = officePathByBrand(themeId, {
							stateStrOptions: {
								stateAbbreviation: eachFacility?.address?.stateCode || '',
								state: '',
								city: eachFacility.address?.city || '',
							},
							cityAddressOptions: {
								city: eachFacility.address?.city ?? undefined,
								address: eachFacility.address?.address1 ?? undefined,
								zip: eachFacility.address?.zipCode ?? undefined,
							},
						});

						const isSelectedLocation =
							eachFacility.location?.latitude === currentFacilityLatitude &&
							eachFacility.location?.longitude === currentFacilityLongitude;

						if (!eachFacility.location?.latitude || !eachFacility.location?.longitude) return null;
						return (
							<AdvancedMarker
								key={`${title}-${index}`}
								title={title || ''}
								position={{
									lat: eachFacility.location.latitude,
									lng: eachFacility.location.longitude,
								}}
								clickable={true}
								onClick={() => handleMarkerClick(facilityUrl)}
							>
								<img
									src={createSVGMarkerObject(getMarkerIcon(facilityBrand, isSelectedLocation))}
									alt=""
								/>
							</AdvancedMarker>
						);
					})}
				</Map>
			</Grid>
		</APIProvider>
	);
});
